import type { RouteRecordInfo } from 'vue-router'

import type { PaymentMethodCode as BuybackPaymentMethodCode } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'

import { PaymentFlow } from './flows'

/**
 * The prefix to use for all payment routes.
 * This is used to prefix all payment routes so that we can configure the ingress to redirect to the payment app.
 */
const APP_ROUTING_PREFIX = 'payment'

/**
 * Known paths of payment pages on the payment subdomain for each flow.
 *
 */
export const PaymentRoutePaths: Record<PaymentFlow, RouteRecordInfo['path']> = {
  [PaymentFlow.CHECKOUT_CART]: `${APP_ROUTING_PREFIX}/checkout/cart`,
  [PaymentFlow.INSURANCE_DUE_AMOUNT]: `${APP_ROUTING_PREFIX}/post-purchase/insurance/:policyId/due`,
  [PaymentFlow.BUYBACK_SELLER_UPDATE_PAYMENT_METHOD]: `${APP_ROUTING_PREFIX}/seller/buyback/update-payment-method/:code`,
} as const

/**
 * Helpers to build the path of a payment page based on the flow parameters.
 */
const PaymentRouteBuilders: {
  [Flow in PaymentFlow]: (params: PaymentRouteParams<Flow>) => string
} = {
  [PaymentFlow.CHECKOUT_CART]: () =>
    PaymentRoutePaths[PaymentFlow.CHECKOUT_CART],
  [PaymentFlow.INSURANCE_DUE_AMOUNT]: (params) =>
    PaymentRoutePaths[PaymentFlow.INSURANCE_DUE_AMOUNT].replace(
      ':policyId',
      params.policyId,
    ),
  [PaymentFlow.BUYBACK_SELLER_UPDATE_PAYMENT_METHOD]: (params) =>
    PaymentRoutePaths[PaymentFlow.BUYBACK_SELLER_UPDATE_PAYMENT_METHOD].replace(
      ':code',
      params.code,
    ),
} as const

/**
 * Build the relativepath of a payment page based on the flow parameters.
 */
export const buildPaymentRoute = <Flow extends PaymentFlow>(
  flow: Flow,
  params: PaymentRouteParams<Flow>,
) => PaymentRouteBuilders[flow](params)

type PaymentFlowParamTypes = {
  [PaymentFlow.INSURANCE_DUE_AMOUNT]: {
    policyId: string
  }
  [PaymentFlow.BUYBACK_SELLER_UPDATE_PAYMENT_METHOD]: {
    code: BuybackPaymentMethodCode
  }
}

/**
 * Type to help the autocomplete of the flow parameters based on the flow.
 */
export type PaymentRouteParams<Flow extends PaymentFlow> =
  Flow extends keyof PaymentFlowParamTypes ? PaymentFlowParamTypes[Flow] : never
